/**
 * IndexedDB Media Cache
 * Handles storage and retrieval of large media files in IndexedDB
 * Used for videos and large images (>5MB)
 */

'use client';

import { persistentMediaCache } from './persistent-media-cache';

/**
 * Store media in IndexedDB if it's large enough
 */
export async function storeMediaInIndexedDB(url: string, response: Response): Promise<boolean> {
  try {
    const blob = await response.blob();
    const size = blob.size;

    // Only store if it should use IndexedDB
    if (persistentMediaCache.shouldUseIndexedDB(size, url)) {
      return await persistentMediaCache.storeMedia(url, blob);
    }

    return false;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[IndexedDBMediaCache] Failed to store media:', error);
    }
    return false;
  }
}

/**
 * Get media from IndexedDB
 */
export async function getMediaFromIndexedDB(url: string): Promise<Blob | null> {
  return await persistentMediaCache.getMedia(url);
}

/**
 * Check if media is in IndexedDB
 */
export async function hasMediaInIndexedDB(url: string): Promise<boolean> {
  return await persistentMediaCache.hasMedia(url);
}

/**
 * Create a blob URL from IndexedDB media
 */
export async function getMediaBlobUrl(url: string): Promise<string | null> {
  const blob = await getMediaFromIndexedDB(url);
  if (blob) {
    return URL.createObjectURL(blob);
  }
  return null;
}
